/**
 * MenuEditor component
 * 7-day grid for editing weekly menu with lunch/dinner slots
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MealSelector } from './MealSelector';
import { AvailabilitySummary } from './AvailabilitySummary';
import { MealPopularityCard } from './MealPopularityCard';
import {
  useWeekMenu,
  useAvailabilitySummary,
  useCreateMenu,
  useUpdateMenu,
  useDeleteMenu,
} from '@/hooks/useMenus';
import { useMenuSuggestions } from '@/hooks/useMenuSuggestions';
import type { Meal, MealType, WeekDay } from '@/types/api';
import { formatDateForDisplay, formatDateForAPI } from '@/lib/utils/date';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

export interface MenuEditorProps {
  /** Start date of the week (Monday) */
  weekStart: Date;
  /** User ID of the organizer */
  organizerId: number;
  /** Callback when save is successful */
  onSaveSuccess?: () => void;
}

interface MenuSlotState {
  date: string;
  mealType: MealType;
  menuId?: number;
  mealId?: number;
}

/**
 * Menu editor with 7-day grid layout
 *
 * Features:
 * - 7-day week view (Monday-Sunday)
 * - Lunch and dinner slots per day
 * - Click to edit meal selection
 * - Remove menu entry button
 * - Availability summary per slot
 * - Real-time updates
 *
 * @example
 * ```tsx
 * function EditMenu() {
 *   const [weekStart, setWeekStart] = useState(getWeekStart(new Date()));
 *   const { user } = useAuth();
 *
 *   return (
 *     <MenuEditor
 *       weekStart={weekStart}
 *       organizerId={user.id}
 *       onSaveSuccess={() => console.log('Saved!')}
 *     />
 *   );
 * }
 * ```
 */
export function MenuEditor({
  weekStart,
  organizerId,
  onSaveSuccess,
}: MenuEditorProps) {
  const { t } = useTranslation();
  const [editingSlot, setEditingSlot] = useState<MenuSlotState | null>(null);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [isInsightsPanelExpanded, setIsInsightsPanelExpanded] = useState(false);

  const weekStartDate = formatDateForAPI(weekStart);

  // Fetch menu suggestions
  const {
    data: suggestions,
    isLoading: isSuggestionsLoading,
  } = useMenuSuggestions({
    weekStartDate,
    enabled: true,
  });

  // Fetch week menu
  const { data: menuData, isLoading: isMenuLoading } = useWeekMenu({
    startDate: weekStartDate,
  });

  // Fetch availability summary
  const { data: availabilityData, isLoading: isAvailabilityLoading } =
    useAvailabilitySummary({
      startDate: menuData?.week.startDate || weekStartDate,
      endDate:
        menuData?.week.endDate ||
        formatDateForAPI(
          new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000)
        ),
    });

  // Mutations
  const createMenuMutation = useCreateMenu();
  const updateMenuMutation = useUpdateMenu();
  const deleteMenuMutation = useDeleteMenu();

  const isLoading = isMenuLoading || isAvailabilityLoading;
  const isSaving =
    createMenuMutation.isPending ||
    updateMenuMutation.isPending ||
    deleteMenuMutation.isPending;

  // Get availability summary for a specific slot
  const getSlotAvailability = (date: string, mealType: MealType) => {
    return availabilityData?.summary.find(
      (item) => item.date === date && item.mealType === mealType
    );
  };

  // Handle opening meal selector
  const handleEditSlot = (day: WeekDay, mealType: MealType) => {
    const menu = mealType === 'lunch' ? day.lunch : day.dinner;

    setEditingSlot({
      date: day.date,
      mealType,
      menuId: menu?.id,
      mealId: menu?.meal.id,
    });
    setIsSelectorOpen(true);
  };

  // Handle meal selection
  const handleMealSelect = async (meal: Meal) => {
    if (!editingSlot) return;

    try {
      // If meal id is 0 or empty, it means clear selection
      if (!meal.id || meal.id === 0) {
        if (editingSlot.menuId) {
          await deleteMenuMutation.mutateAsync(editingSlot.menuId);
        }
      } else if (editingSlot.menuId) {
        // Update existing menu
        await updateMenuMutation.mutateAsync({
          id: editingSlot.menuId,
          data: { mealId: meal.id },
        });
      } else {
        // Create new menu
        await createMenuMutation.mutateAsync({
          date: editingSlot.date,
          mealType: editingSlot.mealType,
          mealId: meal.id,
          createdBy: organizerId,
        });
      }

      setIsSelectorOpen(false);
      setEditingSlot(null);
      onSaveSuccess?.();
    } catch (error) {
      console.error('Error saving menu:', error);
    }
  };

  // Handle remove menu entry
  const handleRemoveMenu = async (menuId: number) => {
    if (confirm(t('menu.confirmRemove'))) {
      try {
        await deleteMenuMutation.mutateAsync(menuId);
        onSaveSuccess?.();
      } catch (error) {
        console.error('Error removing menu:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
        {Array.from({ length: 7 }).map((_, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-md p-4 h-64 animate-pulse"
          >
            <div className="h-6 bg-gray-200 rounded w-1/2 mx-auto mb-4" />
            <div className="space-y-3">
              <div className="h-20 bg-gray-200 rounded" />
              <div className="h-20 bg-gray-200 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!menuData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('menu.unableToLoad')}</p>
      </div>
    );
  }

  // Handle meal selection from insights panel
  const handleInsightMealSelect = (_mealId: number) => {
    // For now, just open the selector
    // In a more advanced implementation, we could allow quick-add
    setIsSelectorOpen(true);
  };

  // Check if we have suggestions data
  const hasSuggestions =
    suggestions &&
    suggestions.totalUsersWithSelections > 0 &&
    suggestions.popularMeals.length > 0;

  return (
    <>
      {/* Weekly Meal Insights Panel */}
      {hasSuggestions && !isSuggestionsLoading && (
        <div className="mb-6 bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
          <button
            type="button"
            onClick={() => setIsInsightsPanelExpanded(!isInsightsPanelExpanded)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <svg
                className="w-6 h-6 text-primary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <div className="text-left">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('menu.weeklyInsights')}
                </h3>
                <p className="text-sm text-gray-600">
                  {suggestions.totalUsersWithSelections}{' '}
                  {suggestions.totalUsersWithSelections === 1
                    ? t('menu.personHas')
                    : t('menu.peopleHave')}{' '}
                  {t('menu.selected')} {suggestions.totalSelectionsCount} {suggestions.totalSelectionsCount === 1 ? t('menu.meal') : t('menu.meals')} {t('menu.thisWeek')}
                </p>
              </div>
            </div>
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform ${
                isInsightsPanelExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isInsightsPanelExpanded && (
            <div className="px-6 pb-6 border-t border-gray-200">
              <div className="mt-4 space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">
                    {t('menu.topMostPopular', { count: Math.min(suggestions.popularMeals.length, 5) })}
                  </h4>
                  <div className="space-y-3">
                    {suggestions.popularMeals.slice(0, 5).map((meal) => (
                      <MealPopularityCard
                        key={meal.mealId}
                        meal={meal}
                        onSelect={handleInsightMealSelect}
                      />
                    ))}
                  </div>
                </div>

                {suggestions.userSelectionsSummary.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">
                      {t('menu.userSelectionsSummary')}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {suggestions.userSelectionsSummary.map((userSummary) => (
                        <div
                          key={userSummary.userId}
                          className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">
                                {userSummary.userName}
                              </span>
                              {userSummary.isOrganizer && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                                  {t('header.organizer')}
                                </span>
                              )}
                            </div>
                            <span className="text-sm font-semibold text-primary-600">
                              {userSummary.totalMealsSelected} {userSummary.totalMealsSelected === 1 ? t('menu.meal') : t('menu.meals')}
                            </span>
                          </div>
                          <div className="space-y-1">
                            {userSummary.selectedMeals.map((meal) => (
                              <div
                                key={meal.mealId}
                                className="flex items-center justify-between text-sm"
                              >
                                <span className="text-gray-700">{meal.mealName}</span>
                                <span className="text-gray-500 text-xs">
                                  {meal.selectionCount}x
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
        {menuData.menus.map((day) => {
          const dayDate = parseISO(day.date);
          const dayName = format(dayDate, 'EEEE', { locale: fr });
          const dayDisplay = formatDateForDisplay(dayDate);

          // Calculate if day is in the past
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          // Use local date instead of UTC to avoid timezone issues
          const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
          const isPast = day.date < todayString;

          return (
            <div
              key={day.date}
              className={`bg-white rounded-lg shadow-md overflow-hidden transition-opacity ${isPast ? 'opacity-50' : ''}`}
            >
              {/* Day header */}
              <div className="bg-primary-700 text-white p-3 text-center">
                <div className="font-bold text-sm">{dayName}</div>
                <div className="text-xs opacity-90">{dayDisplay}</div>
              </div>

              {/* Lunch slot */}
              <div className="p-3 border-b border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase">
                    {t('availability.lunch')}
                  </h4>
                  <AvailabilitySummary
                    summary={getSlotAvailability(day.date, 'lunch')}
                    isLoading={isAvailabilityLoading}
                    compact
                  />
                </div>

                {day.lunch ? (
                  <div className="space-y-2">
                    <div className="p-2 bg-gray-50 rounded border border-gray-200">
                      <p className="text-sm font-medium text-gray-900">
                        {day.lunch.meal.name}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {day.lunch.meal.tags
                          .filter((tag) => !tag.isHidden)
                          .slice(0, 2)
                          .map((tag) => (
                            <span
                              key={tag.id}
                              className="inline-block px-1.5 py-0.5 text-xs bg-gray-200 text-gray-700 rounded"
                            >
                              {tag.tag}
                            </span>
                          ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleEditSlot(day, 'lunch')}
                        disabled={isSaving}
                        className="flex-1 px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50 transition-colors"
                      >
                        {t('menu.change')}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveMenu(day.lunch!.id)}
                        disabled={isSaving}
                        className="px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50 transition-colors"
                      >
                        {t('menu.remove')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleEditSlot(day, 'lunch')}
                    disabled={isSaving}
                    className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-primary-400 hover:text-primary-600 transition-colors disabled:opacity-50"
                  >
                    {t('menu.addMeal')}
                  </button>
                )}
              </div>

              {/* Dinner slot */}
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase">
                    {t('availability.dinner')}
                  </h4>
                  <AvailabilitySummary
                    summary={getSlotAvailability(day.date, 'dinner')}
                    isLoading={isAvailabilityLoading}
                    compact
                  />
                </div>

                {day.dinner ? (
                  <div className="space-y-2">
                    <div className="p-2 bg-gray-50 rounded border border-gray-200">
                      <p className="text-sm font-medium text-gray-900">
                        {day.dinner.meal.name}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {day.dinner.meal.tags
                          .filter((tag) => !tag.isHidden)
                          .slice(0, 2)
                          .map((tag) => (
                            <span
                              key={tag.id}
                              className="inline-block px-1.5 py-0.5 text-xs bg-gray-200 text-gray-700 rounded"
                            >
                              {tag.tag}
                            </span>
                          ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleEditSlot(day, 'dinner')}
                        disabled={isSaving}
                        className="flex-1 px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50 transition-colors"
                      >
                        {t('menu.change')}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveMenu(day.dinner!.id)}
                        disabled={isSaving}
                        className="px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50 transition-colors"
                      >
                        {t('menu.remove')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleEditSlot(day, 'dinner')}
                    disabled={isSaving}
                    className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-primary-400 hover:text-primary-600 transition-colors disabled:opacity-50"
                  >
                    {t('menu.addMeal')}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Meal selector modal */}
      {isSelectorOpen && editingSlot && (
        <MealSelector
          isOpen={isSelectorOpen}
          onClose={() => {
            setIsSelectorOpen(false);
            setEditingSlot(null);
          }}
          onSelect={handleMealSelect}
          selectedMealId={editingSlot.mealId}
          title={t('menu.selectMealFor', {
            mealType: editingSlot.mealType === 'lunch' ? t('availability.lunch') : t('availability.dinner')
          })}
          date={editingSlot.date}
          mealType={editingSlot.mealType}
          availabilitySummary={availabilityData?.summary}
        />
      )}
    </>
  );
}
